import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  assignChartScan,
  deleteChartDisc,
  deleteChartScan,
  getChartDisc,
  getChartDiscDiagnostic,
  getChartScan,
  getChartScans,
  updateChartDisc,
  uploadChartScan,
} from './api'
import { AssignChartScanPayload, ChartScanListFilter, UpdateDiscPayload } from './types'

const keys = {
  list: (companyId: string | number) => ['chart-scans', String(companyId)] as const,
  scan: (companyId: string | number, scanId: number) => ['chart-scan', companyId, scanId] as const,
  disc: (companyId: string | number, discId: number) => ['chart-scan-disc', companyId, discId] as const,
}

export function useChartScans(companyId: string | number, filter: ChartScanListFilter, page: number) {
  return useQuery({
    queryKey: [...keys.list(companyId), filter, page],
    queryFn: () => getChartScans(companyId, filter, page).then((r) => r.data),
    enabled: !!companyId,
  })
}

// Load a single chart by disc id (for the daily-activity "Pokaż tarczkę" modal).
export function useChartDisc(
  companyId: string | number,
  discId: number | null,
  includeSiblings = false,
) {
  return useQuery({
    // includeSiblings changes the shape of the response, so it has to be part of
    // the key — otherwise the single-disc and whole-scan variants share a cache
    // entry and whichever loads first wins.
    queryKey: [...keys.disc(companyId, discId ?? -1), includeSiblings] as const,
    queryFn: () =>
      getChartDisc(companyId, discId as number, includeSiblings).then((r) => r.data),
    enabled: discId != null,
  })
}

export function useUploadChartScan(companyId: string | number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadChartScan(companyId, file).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
      qc.invalidateQueries({ queryKey: ['QUERY_KEY_HISTORICAL_FILES', String(companyId)] })
    },
  })
}

export function useChartScan(companyId: string | number, scanId: number | null) {
  return useQuery({
    queryKey: keys.scan(companyId, scanId ?? -1),
    queryFn: () => getChartScan(companyId, scanId as number).then((r) => r.data),
    enabled: scanId != null,
  })
}

export function useUpdateDisc(companyId: string | number, scanId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ discId, payload }: { discId: number; payload: UpdateDiscPayload }) =>
      updateChartDisc(companyId, discId, payload).then((r) => r.data),
    onSuccess: (resp) => {
      // Only invalidate when we actually saved (not when the server returned conflicts).
      if (resp.saved) {
        qc.invalidateQueries({ queryKey: keys.scan(companyId, scanId) })
        qc.invalidateQueries({ queryKey: ['chart-scan-disc', companyId] })
        qc.invalidateQueries({ queryKey: keys.list(companyId) })
      }
    },
  })
}

export function useDiscDiagnostic(
  companyId: string | number,
  discId: number | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['chart-scan', 'diagnostic', companyId, discId ?? -1] as const,
    queryFn: () => getChartDiscDiagnostic(companyId, discId as number).then((r) => r.data),
    enabled: enabled && discId != null,
  })
}

export function useDeleteDisc(companyId: string | number, scanId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (discId: number) => deleteChartDisc(companyId, discId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.scan(companyId, scanId) })
      qc.invalidateQueries({ queryKey: ['chart-scan-disc', companyId] })
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
    },
  })
}


export function useDeleteDiscs(companyId: string | number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ discId }: { scanId: number; discId: number }) =>
      deleteChartDisc(companyId, discId),
    onSuccess: (_data, { scanId }) => {
      qc.invalidateQueries({ queryKey: keys.scan(companyId, scanId) })
      qc.invalidateQueries({ queryKey: ['chart-scan-disc', companyId] })
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
    },
  })
}

export function useDeleteChartScan(companyId: string | number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (scanId: number) => deleteChartScan(companyId, scanId),
    onSuccess: (_data, scanId) => {
      qc.removeQueries({ queryKey: keys.scan(companyId, scanId) })
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
    },
  })
}

// Multi-file variant: each selected file becomes its own scan, so the assign step
// saves one payload per scan. scanId travels in the mutation variables instead of
// being bound when the hook is created.
export function useAssignChartScans(companyId: string | number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ scanId, payload }: { scanId: number; payload: AssignChartScanPayload }) =>
      assignChartScan(companyId, scanId, payload).then((r) => r.data),
    onSuccess: (_data, { scanId }) => {
      qc.invalidateQueries({ queryKey: keys.scan(companyId, scanId) })
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
    },
  })
}

export function useAssignChartScan(companyId: string | number, scanId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AssignChartScanPayload) =>
      assignChartScan(companyId, scanId, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.scan(companyId, scanId) })
      qc.invalidateQueries({ queryKey: keys.list(companyId) })
    },
  })
}
